import { useState } from "react";
import { Button, ModalDelete } from "expano-components";
import { connect } from "react-redux";
import Loader from "react-loader-spinner";

import { client_v2 } from "utils/api";
import getLocationDataFromIdentifier from "utils/getLocationDataFromIdentifier";
import { createInventarizationTask } from "actions/products";
import { deleteMainLocation } from "actions/product_main_locations";

import { ReactComponent as ClockIcon } from "icons/clock.svg";
import Logs from "../../modals/Logs";
import { EditLocation } from "../../modals";
import LastActivity from "../../modals/LastActivity";
import OtherRealStockDataModal from "./components/OtherRealStockDataModal";

const getStockStatusColor = (diff) => {
  if (diff > 0) {
    return "stock-positive";
  }
  if (diff < 0) {
    return "stock-negative";
  }

  return "stock-neutral";
};

const getDecomposedQuantity = (warehouse_id, product_suppliers) => {
  if (warehouse_id !== 12) {
    return 0;
  }
  const product_supplier = product_suppliers.find(
    ({ is_master, is_product_decomposed }) => is_master && is_product_decomposed
  );

  return product_supplier?.decomposed_quantity || 0;
};

const getComposedQuantity = (warehouse_id, product_suppliers) => {
  if (warehouse_id !== 12) {
    return 0;
  }
  const product_supplier = product_suppliers.find(
    ({ is_master, is_product_composed }) => is_master && is_product_composed
  );

  return product_supplier?.composed_quantity || 0;
};

const LineItem = ({
  id,
  product_id,
  stock,
  stock_diff = 0,
  warehouse,
  warehouse_position,
  has_pending_inventarization_task,
  createInventarizationTask,
  deleteMainLocation,
  product_suppliers,
  user_role,
}) => {
  const [is_loading_real_stock, setLoadingRealStock] = useState(false);
  const [is_adding_task, setIsAddingTask] = useState(false);
  const [shown_last_activity, showLastActivity] = useState(false);
  const [shown_logs, showLogs] = useState(false);
  const [shown_edit, showEdit] = useState(false);
  const [shown_delete, showDelete] = useState(false);
  const [real_stock, setRealStock] = useState("b/d");
  const [other_data, setOtherData] = useState(null);
  const location_data = getLocationDataFromIdentifier(
    warehouse_position?.identifier
  );

  const getRealStock = async () => {
    try {
      setLoadingRealStock(true);
      const { data } = await client_v2.get(
        `/products/${product_id}/real_stock?warehouse_id=${warehouse.id}`
      );

      setRealStock(data.real_stock);
      if (user_role === "admin") {
        setOtherData(data);
      }
    } finally {
      setLoadingRealStock(false);
    }
  };

  const handleCreateTask = async () => {
    try {
      setIsAddingTask(true);
      await createInventarizationTask({
        product_main_location_id: id,
        product_id,
      });
    } catch (error) {
      console.log(error);
    } finally {
      setIsAddingTask(false);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteMainLocation(id);
    } catch (error) {
      console.log(error);
    } finally {
      showDelete(false);
    }
  };

  const is_editable = [1, 2].includes(warehouse?.id);

  const decomposed_quantity = getDecomposedQuantity(
    warehouse?.id,
    product_suppliers
  );

  const composed_quantity = getComposedQuantity(
    warehouse?.id,
    product_suppliers
  );

  return (
    <li className="product-localizations__item">
      {shown_delete && (
        <ModalDelete
          handleDelete={handleDelete}
          handleClose={() => showDelete(false)}
          name={`lokalizację ${warehouse_position.identifier}`}
        />
      )}
      {is_editable && shown_edit && (
        <EditLocation
          handleClose={() => showEdit(false)}
          id={id}
          initial_warehouse_position_id={warehouse_position?.id}
        />
      )}
      {shown_logs && <Logs handleClose={() => showLogs(false)} id={id} />}
      {is_editable && shown_last_activity && (
        <LastActivity
          handleClose={() => showLastActivity(false)}
          id={warehouse_position?.id}
        />
      )}
      {other_data && (
        <OtherRealStockDataModal
          data={other_data}
          handleClose={() => setOtherData(null)}
        />
      )}
      <div className="text">
        <p className="name">{warehouse?.name}</p>
        <ul className="address">
          <li>Pomieszczenie {location_data?.room || "-"}</li>
          <li>Alejka {location_data?.aisle || "-"}</li>
          <li>Regał {location_data?.rack}</li>
          <li>Półka {location_data?.shelf}</li>
          <li>Pozycja {location_data?.position}</li>
        </ul>
        <div className="stock">
          <p>
            Ilość na półce: <span>{real_stock}</span>{" "}
            {is_editable && (
              <button
                type="button"
                onClick={getRealStock}
                disabled={is_loading_real_stock}
              >
                {is_loading_real_stock ? (
                  <Loader type="Oval" color="#5d78ff" height={15} width={15} />
                ) : (
                  <ClockIcon />
                )}
              </button>
            )}
          </p>
          <p>
            Na stanie:{" "}
            <span>
              {typeof stock === "number" ? (
                <>
                  {stock} szt.
                  <span className={getStockStatusColor(stock_diff)}>
                    ({stock_diff > 0 ? "+" : ""}
                    {stock_diff})
                  </span>
                </>
              ) : (
                "b/d"
              )}
            </span>
          </p>
          {decomposed_quantity > 0 && (
            <p>
              Dekompletacja: <span>{decomposed_quantity} szt.</span>
            </p>
          )}
          {composed_quantity > 0 && (
            <p>
              Kompletacja: <span>{composed_quantity} szt.</span>
            </p>
          )}
        </div>
      </div>
      {is_editable && (
        <div style={{ display: "flex", alignItems: "center" }}>
          <Button
            style={{ marginRight: 10 }}
            type="add"
            disabled={has_pending_inventarization_task || is_adding_task}
            text={
              has_pending_inventarization_task
                ? "Task utworzony"
                : "Utwórz task inwentaryzacyjny"
            }
            onClick={handleCreateTask}
          />
          <Button
            style={{ marginRight: 10 }}
            text="Historia inwentaryzacji"
            onClick={() => showLogs(true)}
          />
          <Button
            text="Ostatnia aktywność"
            onClick={() => showLastActivity(true)}
          />
          <Button type="edit" onClick={() => showEdit(true)} />
          {warehouse_position?.identifier !== "B01B01-R01P01M01" && (
            <Button type="delete-bin" onClick={() => showDelete(true)} />
          )}
        </div>
      )}
    </li>
  );
};

export default connect(
  ({ edit_product, current_user: { role } }) => ({
    product_suppliers: edit_product?.data?.product_suppliers,
    user_role: role,
  }),
  (dispatch) => ({
    createInventarizationTask: (id) => dispatch(createInventarizationTask(id)),
    deleteMainLocation: (id) => dispatch(deleteMainLocation(id)),
  })
)(LineItem);
